import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";

export type ToastKind = "success" | "error" | "info";

export interface ToastInput {
  kind: ToastKind;
  title: string;
  description?: string;
  /** ms before auto-dismiss. 0 = persistent.
   *  Defaults: 5000 for success/info, 0 for error. */
  duration?: number;
}

interface Toast extends ToastInput {
  id: number;
}

interface ToastContextValue {
  push: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
  toasts: ReadonlyArray<Toast>;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: ToastInput): number => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { ...toast, id }]);
      const ms =
        toast.duration ?? (toast.kind === "error" ? 0 : 5000);
      if (ms > 0) {
        window.setTimeout(() => dismiss(id), ms);
      }
      return id;
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ push, dismiss, toasts }}>
      {children}
    </ToastContext.Provider>
  );
}

const KIND_STYLES: Record<
  ToastKind,
  { accent: string; iconColor: string; icon: typeof CheckCircle2 }
> = {
  success: {
    accent: "border-l-sage",
    iconColor: "text-sage",
    icon: CheckCircle2,
  },
  error: {
    accent: "border-l-rust",
    iconColor: "text-rust",
    icon: AlertCircle,
  },
  info: {
    accent: "border-l-tide",
    iconColor: "text-tide",
    icon: Info,
  },
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: () => void;
}) {
  const styles = KIND_STYLES[toast.kind];
  const Icon = styles.icon;
  return (
    <div
      role={toast.kind === "error" ? "alert" : "status"}
      aria-live={toast.kind === "error" ? "assertive" : "polite"}
      className={`pointer-events-auto rounded-md shadow-lift bg-paper-hi border border-l-4 hair ${styles.accent} px-4 py-3 flex items-start gap-3 portal-reveal portal-reveal-1`}
    >
      <Icon
        size={16}
        strokeWidth={2}
        className={`${styles.iconColor} flex-shrink-0 mt-0.5`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium leading-snug">{toast.title}</div>
        {toast.description && (
          <div className="text-[12px] text-ink-3 mt-0.5 leading-snug">
            {toast.description}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="text-ink-4 hover:text-ink-2 flex-shrink-0 mt-0.5"
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

/**
 * Renders the active toast stack pinned to the bottom-right of the
 * viewport. Newest toast is closest to the corner. Capped at 20vw
 * wide so the stack never dominates the screen.
 */
export function ToastViewport() {
  const { toasts, dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed z-50 pointer-events-none flex flex-col-reverse gap-2"
      style={{
        bottom: "1.5rem",
        right: "1.5rem",
        width: "20vw",
        minWidth: 280,
        maxWidth: 380,
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

/**
 * Helper for callers who only need to fire a toast without reading the
 * stack. Returns `useToast().push` for shorter call sites.
 */
export function useToastPush() {
  const { push } = useToast();
  return push;
}
